import { useEffect, useState } from "react";
import ListenStats from "./ListenStats";
import AudioWorkerPanel from "./AudioWorkerPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel({ secret }) {
  const [tab, setTab] = useState("comments");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/${secret}/comments`)
      .then((r) => {
        if (r.status === 404) throw new Error("Invalid admin link");
        return r.json();
      })
      .then((d) => {
        setComments(Array.isArray(d) ? d : []);
        setErr("");
      })
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [secret]);

  const remove = async (id) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    setBusyId(id);
    try {
      const r = await fetch(`${API}/admin/${secret}/comments/${id}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error("Delete failed");
      setComments((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {
      alert(e.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-root" data-testid="admin-root">
      <header className="admin-header">
        <h1 className="admin-title">Twice Built — Admin</h1>
        <div className="admin-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "comments"}
            className={"admin-tab" + (tab === "comments" ? " admin-tab-active" : "")}
            onClick={() => setTab("comments")}
            data-testid="admin-tab-comments"
          >
            Comments
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "listens"}
            className={"admin-tab" + (tab === "listens" ? " admin-tab-active" : "")}
            onClick={() => setTab("listens")}
            data-testid="admin-tab-listens"
          >
            Listen analytics
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "audio"}
            className={"admin-tab" + (tab === "audio" ? " admin-tab-active" : "")}
            onClick={() => setTab("audio")}
            data-testid="admin-tab-audio"
          >
            Audio pipeline
          </button>
        </div>
        <div className="admin-actions">
          {tab === "comments" && (
            <>
              <span className="admin-count" data-testid="admin-count">
                {comments.length} comments
              </span>
              <button onClick={load} className="admin-btn" data-testid="admin-refresh">
                Refresh
              </button>
            </>
          )}
          <a href="/" className="admin-btn admin-btn-ghost">← Back to site</a>
        </div>
      </header>

      {tab === "listens" && <ListenStats secret={secret} />}
      {tab === "audio" && <AudioWorkerPanel secret={secret} />}

      {tab === "comments" && (
        <>
          {err && <div className="admin-err" data-testid="admin-err">{err}</div>}
          {loading && <div className="admin-muted">Loading…</div>}

          <ul className="admin-list">
        {comments.map((c) => (
          <li key={c.id} className="admin-card" data-testid={`admin-comment-${c.id}`}>
            <div className="admin-card-head">
              <span className="admin-card-name">{c.name}</span>
              <span className="admin-card-ts">
                {new Date(c.ts).toLocaleString()}
              </span>
            </div>
            <div className="admin-card-msg">{c.message}</div>
            <div className="admin-card-foot">
              <code className="admin-card-id">{c.id}</code>
              <button
                onClick={() => remove(c.id)}
                disabled={busyId === c.id}
                className="admin-btn admin-btn-danger"
                data-testid={`admin-delete-${c.id}`}
              >
                {busyId === c.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </li>
        ))}
        {!loading && comments.length === 0 && !err && (
          <li className="admin-muted">No comments yet.</li>
        )}
      </ul>
        </>
      )}
    </div>
  );
}
